import React from "react";

import { useAuth } from "@/contexts/AuthContext.jsx";
import { loadPatientAppointmentSnapshot } from "@/services/patientAppointmentSnapshots.js";
import { loadDoctorAppointmentSnapshot } from "@/pages/doctors/appointments/lib/doctorAppointmentsData.js";

function safeJSONParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readSnapshotFromSession(id, navState) {
  if (navState && Object.keys(navState).length > 0) return navState;

  try {
    if (id) {
      const rawById = sessionStorage.getItem(`afrimed:appointment:${String(id)}`);
      const parsedById = rawById ? safeJSONParse(rawById) : null;
      if (parsedById) return parsedById;
    }
    const rawLast = sessionStorage.getItem("afrimed:appointment:last");
    return rawLast ? safeJSONParse(rawLast) : null;
  } catch {
    return null;
  }
}

function persistSnapshot(id, payload) {
  if (!id || !payload) return;
  try {
    sessionStorage.setItem(`afrimed:appointment:${String(id)}`, JSON.stringify(payload));
    sessionStorage.setItem("afrimed:appointment:last", JSON.stringify(payload));
  } catch {
    // ignore
  }
}

function normalizeRole(auth, routeState, restored) {
  const explicit = String(routeState?.actorRole || restored?.actorRole || "").trim().toLowerCase();
  if (explicit === "doctor" || explicit === "patient") return explicit;

  const profileRole = String(auth?.profile?.role || "").trim().toLowerCase();
  if (profileRole === "doctor" || profileRole === "patient") return profileRole;

  if (auth?.profile?.hasDoctor && !auth?.profile?.hasPatient) return "doctor";
  if (!auth?.profile?.hasDoctor && auth?.profile?.hasPatient) return "patient";
  return "patient";
}

function looksLikeUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim()
  );
}

export default function useAppointmentCallSnapshot(id, routeState) {
  const auth = useAuth() || {};
  const profileRole = auth?.profile?.role || "";
  const hasDoctor = !!auth?.profile?.hasDoctor;
  const hasPatient = !!auth?.profile?.hasPatient;
  const authUserId = auth?.user?.id || "";
  const restored = React.useMemo(
    () => readSnapshotFromSession(id, routeState || null),
    [id, routeState]
  );
  const [snapshot, setSnapshot] = React.useState(restored);
  const [loading, setLoading] = React.useState(Boolean(id));
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let alive = true;

    async function load() {
      if (!id) {
        if (alive) setLoading(false);
        return;
      }

      if (!looksLikeUuid(id)) {
        if (!alive) return;
        setSnapshot(restored || routeState || null);
        setError("");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      const role = normalizeRole(
        { profile: { role: profileRole, hasDoctor, hasPatient } },
        routeState,
        restored
      );

      try {
        let next = null;

        if (role === "doctor" && authUserId) {
          next = await loadDoctorAppointmentSnapshot({
            appointmentId: id,
            doctorId: authUserId,
            facilityName:
              restored?.facilityName ||
              routeState?.facilityName ||
              "Consultation location",
          });
        }

        if (!next) {
          next = await loadPatientAppointmentSnapshot(id, { fallback: restored || routeState || null });
        }

        if (!alive) return;
        if (next) {
          const merged = {
            ...(routeState || {}),
            ...(restored || {}),
            ...next,
            actorRole: role,
          };
          setSnapshot(merged);
          persistSnapshot(id, merged);
        } else {
          setSnapshot(restored || routeState || null);
        }
      } catch (e) {
        if (!alive) return;
        setSnapshot(restored || routeState || null);
        if (!restored && !routeState) {
          setError(e?.message || "Unable to load appointment call details.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [id, routeState, restored, profileRole, hasDoctor, hasPatient, authUserId]);

  return {
    snapshot,
    loading,
    error,
    persist: (payload) => persistSnapshot(id, payload),
  };
}
